import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  OnChanges,
  SimpleChanges,
  ViewContainerRef,
  OnInit
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'avn/services/api.service';
import { Constants } from 'avn/services/constants';
import { StoreService } from 'avn/services/store.service';
import { SharedCompsService } from 'avn/shared/shared-comps.service';
import { toUrlSlug } from 'avn/shared/utils/url-slug.util';
import * as moment from 'moment';
import { BehaviorSubject, Observable, Subject, catchError, debounceTime, map, of, tap } from 'rxjs';

declare let mapboxgl: any;

@Component({
  selector: 'app-map-view',
  templateUrl: './map-view.component.html',
  styleUrls: ['./map-view.component.scss']
})
export class MapViewComponent implements OnDestroy, OnInit, OnChanges {
  // Input and Output properties
  @Input() condition: boolean; // Control condition-based functionality
  @Input() loadingLocations = false; // Flag for loading state of locations
  @Input() selectedDate = moment().format('YYYY-MM-DD');
  @Input() claimNo: string;
  @Input() bookingType: 'In Person' | 'Online' = 'In Person';

  @Output() updateCoordinates = new EventEmitter(); // Event emitter to notify parent component
  @Output() clickedBiz = new EventEmitter(); // Event emitter to notify parent component related on which business hovered
  @Input() fitNonce = 0;
  @Input() bounds: [[number, number], [number, number]] | null = null;

  // Variables
  private mapReady = false;
  center: [number, number] = [144.9630576, -37.8136276]; // Default map center coordinates
  coordinates: any[] = []; // Array to store coordinates data
  featureCollections: any = []; // Array for feature collections
  isMovedCoordinate = false; // Flag indicating user movement on map
  lastHighlight: string; // Stores UUID of last highlighted marker
  lat = -37.8136276; // Default latitude
  loading = false; // Loading state
  locations: any = []; // List of location data
  map: any; // Mapbox map instance
  mapCenterDefault: any; // Default map center
  markers: any[] = []; // Array of markers on the map
  mouseMoveSubject: Subject<any> = new Subject<any>(); // Subject to handle mouse movement debouncing
  movingAuto = false; // Flag indicating automatic map movement
  movedCoordinates: any; // Coordinates after map movement
  provider: any = {}; // Provider data
  radius = 0; // Radius of search area
  zoom = 13; // Default zoom level
  pracUuId = '';
  apploading = false;

  private locationsChange: Subject<void> = new Subject<void>(); // Subject to handle location changes

  notLoaded = false;
  timeSlots$: Observable<any[]> = of([]); // Observable for time slots data
  slotsAvailability$: BehaviorSubject<any> = new BehaviorSubject<any>({}); // Observable to track slots availability
  slotsAvailability: any = {}; // Slots availability data
  extra_params: any = {};

  // Dynamic Input properties
  @Input() set coordinatesList(data: { list: any[]; center: [number, number] }) {
    if (data.list || data.center) {
      this.center = data.center; // Update map center
      this.coordinates = data.list || []; // Update coordinates list
      if (!this.map) {
        this.notLoaded = true;
      } else {
        this.generateLocationListData(this.coordinates); // Generate map data based on coordinates
      }
    }
  }

  @Input() set hoveringCard(uuid: any) {
    uuid ? this.highlightMarker(uuid) : this.removeHighlight(); // Highlight or remove highlight based on input
  }

  constructor(
    private api: ApiService,
    private router: Router,
    private viewContainerRef: ViewContainerRef,
    private store: StoreService,
    private shared: SharedCompsService,
    private route: ActivatedRoute
  ) {
    // Subscribe to location change events with debouncing
    this.locationsChange.pipe(debounceTime(200)).subscribe(() => {
      this.generateLocationListData(this.coordinates);
    });

    // Subscribe to mouse movement events with debouncing
    this.mouseMoveSubject.pipe(debounceTime(1000)).subscribe((coords) => {
      if (!this.movingAuto) {
        this.movedCoordinates = coords;
        this.isMovedCoordinate = true;
        this.calculateRadius();
      }
    });
  }

  // Lifecycle hooks

  // Initialize the map on component initialization
  ngOnInit(): void {
    (mapboxgl as any).accessToken = Constants.MAPBOX_CONFIG.accessToken;
    if (!this.map) {
      this.map = new mapboxgl.Map({
        container: 'map',
        style: Constants.MAPBOX_CONFIG.style,
        center: [this.store.meraLocation.lng, this.store.meraLocation.lat],
        zoom: this.zoom,
        interactive: true
      });
      this.map.addControl(new mapboxgl.NavigationControl());

      this.map.on('load', () => {
        this.mapReady = true;
        if (this.notLoaded) {
          this.notLoaded = false;
          this.generateLocationListData(this.coordinates);
        }
        this.fitToResults(); // fit once on load as well
      });

      this.map.on('click', (e: any) => {
        const target = e.originalEvent.target as HTMLElement;
        if (!target?.closest('.av-marker')) {
          this.closePop(true);
        }
      });
      if (this.notLoaded) {
        this.notLoaded = false;
        this.generateLocationListData(this.coordinates); // Generate map data based on coordinates
      }
    }
    this.route.queryParams.subscribe((params) => {
      this.extra_params = params;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['fitNonce'] || changes['bounds'] || changes['coordinatesList']) && this.mapReady) {
      this.fitToResults();
    }
  }

  // Clean up the map instance on component destruction
  ngOnDestroy() {
    if (this.map) {
      this.map.remove();
    }
  }

  // Methods

  // Add markers to the map based on coordinates
  addMarkers() {
    this.removeMarkers(); // Clear existing markers
    setTimeout(() => {
      for (let i = 0; i < this.coordinates.length; i++) {
        const location = this.coordinates[i];
        const el = document.createElement('div');
        const pracs_length = (location.pracs || []).length;
        el.className = 'av-marker';
        el.id = location.business.uuid;
        el.setAttribute('index', '' + i);
        el.setAttribute('pracs', '' + pracs_length);
        el.innerHTML = `<div class="location-marker pin-${pracs_length > 9 ? '9p' : pracs_length}" pracs="${
          pracs_length > 9 ? '9+' : pracs_length
        }"></div>`;
        el.addEventListener('click', this.markerClickEvent);

        const marker = new mapboxgl.Marker(el).setLngLat([location.coords.lng, location.coords.lat]).addTo(this.map);
        this.markers.push(marker); // Add marker to the array
      }

      this.updateMarkerStyles();
      this.map.on('moveend', this.onMapMoveEnd); // Re-add listeners after markers are added
      this.map.on('mousemove', this.onMapMouseMove);
      this.map.on('touchmove', this.onMapTouchMove);
      requestAnimationFrame(() => this.fitToResults());
    }, 100);
  }

  private computeBoundsFromCoordinates(): [[number, number], [number, number]] | null {
    if (!Array.isArray(this.coordinates) || this.coordinates.length === 0) return null;

    let minLng = Number.POSITIVE_INFINITY;
    let minLat = Number.POSITIVE_INFINITY;
    let maxLng = Number.NEGATIVE_INFINITY;
    let maxLat = Number.NEGATIVE_INFINITY;

    for (const item of this.coordinates) {
      const lng = Number(item?.coords?.lng);
      const lat = Number(item?.coords?.lat);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
    }

    if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return null;

    if (minLng === maxLng && minLat === maxLat) {
      const pad = 0.002;
      minLng -= pad;
      maxLng += pad;
      minLat -= pad;
      maxLat += pad;
    }

    return [
      [minLng, minLat],
      [maxLng, maxLat]
    ];
  }

  private getResponsivePadding() {
    const isMobile = window.innerWidth < 1100;
    return isMobile ? { top: 50, right: 30, bottom: 50, left: 30 } : { top: 60, right: 60, bottom: 60, left: 360 }; // leave room for list
  }

  private fitToResults(): void {
    if (!this.mapReady || !this.map) return;

    const bbox = this.bounds /* parent-provided */ ?? this.computeBoundsFromCoordinates(); /* child-computed */

    if (!bbox) return;

    try {
      this.movingAuto = true; // so your onMapMoveEnd knows this wasn't a user pan
      this.map.fitBounds(bbox as any, {
        padding: this.getResponsivePadding(),
        duration: 500,
        maxZoom: 14
      });
    } catch {
      // ignore to keep UX smooth
    }
  }

  // Calculate distance between two coordinates using Haversine formula
  calculateDistance(lng1: number, lat1: number, lng2: number, lat2: number): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  // Calculate the radius for search area
  calculateRadius() {
    const center = this.map.getCenter();
    const corner = this.map.unproject([0, 0]); // Top-left corner of the map
    this.radius = this.calculateDistance(center.lng, center.lat, corner.lng, corner.lat); // Calculate radius
  }

  // Clear map clusters and markers
  clearClustersAndMarkers() {
    this.map.off('moveend', this.onMapMoveEnd); // Remove event listeners
    this.map.off('mousemove', this.onMapMouseMove);
    this.map.off('touchmove', this.onMapTouchMove);
  }

  // Remove highlight from all markers and reset provider data
  closePop(shouldResetView = false) {
    const hadSelection = !!this.provider?.business?.id;
    this.markers.forEach((m) => m.getElement().classList.remove('marker-highlight'));
    this.provider = {};
    if (shouldResetView && hadSelection) {
      this.fitToResults();
    }
  }

  // Initialize and center the map, then add markers
  createMapBox() {
    this.movingAuto = true;
    if (this.map) {
      this.map.flyTo({ center: this.center }); // Fly to new center
      this.addMarkers();
    } else {
      if (this.map) {
        (this.map as any).on('load', () => {
          this.map.flyTo({ center: this.center }); // Fly to new center
          this.addMarkers();
        });
      }
    }
  }

  // Generate map location data from coordinates
  generateLocationListData(data: any) {
    if (data.length === 0) return;
    this.createMapBox();
  }

  // Navigate to practitioner's profile page
  goToProfile(prac: any) {
    const city = toUrlSlug(this.provider.business.business_location.city);
    const prac_url = `/provider/${this.provider.business.slug}/${city}/practitioner/${prac.uuid}`;
    this.router.navigate([prac_url]); // Navigate to profile URL
  }

  // Highlight marker based on UUID
  highlightMarker(uuid: string) {
    this.lastHighlight = uuid;
    const markerEl = document.getElementById(uuid);
    if (markerEl) {
      markerEl.classList.add('marker-highlight'); // Apply highlight style
    }
  }

  // Handle marker click event
  markerClickEvent = (e: any) => {
    this.closePop();
    setTimeout(() => {
      let business_id = e?.target?.id || '';
      let marker_index = e?.target?.attributes?.index?.value;
      if (e.target.classList.contains('location-marker')) {
        business_id = e?.target?.parentElement?.id || '';
        marker_index = e?.target?.parentElement?.attributes?.index?.value;
      }
      this.provider = this.coordinates.find((x) => x.business.uuid === business_id); // Find provider by ID
      this.movingAuto = true;
      this.map.flyTo({
        center: [this.provider.coords.lng, this.provider.coords.lat],
        offset: [0, -100],
        zoom: this.zoom + 1,
        essential: true
      });
      this.clickedBiz.emit(business_id);
      this.markers[marker_index].getElement().classList.add('marker-highlight'); // Highlight selected marker
    }, 100);
  };

  // Event handler for map movement end
  onMapMoveEnd = () => {
    if (this.movingAuto) {
      this.movingAuto = false;
    } else {
      const center = this.map.getCenter();
      const coords: any = { lat: center.lat, lng: center.lng };
      this.mouseMoveSubject.next(coords); // Emit new coordinates
      this.updateMarkerStyles(); // Update marker styles based on new center
    }
  };

  // Event handler for mouse movement on map
  onMapMouseMove = (e: any) => {
    e.preventDefault();
    if (e.originalEvent?.lngLat?.lat) {
      this.mouseMoveSubject.next({ lat: e.originalEvent?.lngLat?.lat, lng: e.originalEvent?.lngLat?.lng });
    }
  };

  // Event handler for touch movement on map
  onMapTouchMove = (e: any) => {
    if (e.originalEvent.touches.length === 1) {
      const touch = e.originalEvent.touches[0];
      const coords: any = this.map.unproject([touch.clientY, touch.clientX]);
      this.mouseMoveSubject.next({ lat: coords[1], lng: coords[0] });
    }
  };

  // Remove all markers from the map
  removeMarkers() {
    for (const marker of this.markers) {
      marker.remove();
    }
    this.markers = []; // Clear the markers array
  }

  // Remove highlight from the previously highlighted marker
  removeHighlight() {
    if (this.lastHighlight) {
      const markerEl = document.getElementById(this.lastHighlight);
      this.lastHighlight = '';
      if (markerEl) {
        markerEl.classList.remove('marker-highlight'); // Remove highlight style
      }
    }
  }

  // Trigger search based on current location
  searchHere() {
    this.provider = {};
    this.isMovedCoordinate = false;
    this.updateCoordinates.emit({
      ...this.movedCoordinates,
      radius: this.radius
    });
  }

  // Fetch practitioner availability data
  fetchAvailability(practitioner: any) {
    this.loading = true; // Set loading to true
    const timezone = this.shared.fetchOnlyAustraliaTimezone(); // Get Australian timezone
    const converted = this.shared.convertedCurrentTimeWithTimezone(); // Get converted current time with timezone
    const current_date = converted.format('YYYY-MM-DD hh:mm A');
    const setting_data = moment(this.selectedDate).format('YYYY-MM-DD');

    // Fetch earliest available dates from the API
    this.api
      // .earliestDates(this.partner.id, this.partner.service, setting_data, current_date, timezone)
      .earliestDates(practitioner?.id, practitioner?.service, setting_data, current_date, timezone)
      .pipe(
        tap((slotAvail: any) => {
          if (slotAvail.appt_date && practitioner?.service) {
            this.slotsAvailability = slotAvail;
            this.slotsAvailability$.next(slotAvail); // Emit availability data
            if (practitioner?.id && practitioner?.service && this.slotsAvailability.appt_date) {
              this.fetchTimeSlots(current_date, timezone, practitioner); // Fetch time slots if data is available
              this.selectTime(practitioner);
            }
          } else {
            this.loading = false; // Set loading to false if no data available
            this.selectTime(practitioner);
          }
        }),
        catchError(() => {
          this.loading = false; // Stop loading on error
          return of({}); // Return empty object on error
        })
      )
      .subscribe();
  }

  // Fetch available time slots based on availability
  fetchTimeSlots(current_date: string, timezone: string, practitioner: any) {
    const params = {
      start_date: moment(this.slotsAvailability.appt_date, 'YYYY-MM-DD').format('YYYY-MM-DD'),
      end_date: moment(this.slotsAvailability.appt_date, 'YYYY-MM-DD').format('YYYY-MM-DD'),
      current_date: current_date,
      practitioner_id: practitioner?.id,
      service_id: practitioner?.service,
      timezone
    };
    // Call API to get available time slots and process response
    this.timeSlots$ = this.api.getAvailableTimes(params).pipe(
      map((response1: any) => {
        const times = response1[moment(this.slotsAvailability.appt_date).format('YYYY-MM-DD')];
        let timeSlotsList: any[] = [];
        if (times && times.status === 'Open') {
          if (times.Morning && times.Morning.length > 0) {
            timeSlotsList = [...timeSlotsList, ...times.Morning];
          }
          if (times.Afternoon && times.Afternoon.length > 0) {
            timeSlotsList = [...timeSlotsList, ...times.Afternoon];
          }
          if (times.Evening && times.Evening.length > 0) {
            timeSlotsList = [...timeSlotsList, ...times.Evening];
          }
        }
        return timeSlotsList;
      }),
      tap(() => {
        this.loading = false; // Stop loading after time slots are fetched
      }),
      catchError(() => {
        this.loading = false;
        return of([]); // Return an empty array on error
      })
    );
  }

  selectTime(practitioner: any, time?: string) {
    console.info('', this.slotsAvailability, practitioner);
    const service_id = this.slotsAvailability?.service?.uuid || practitioner?.service_uuid;
    const prac_id = this.slotsAvailability?.practitioner?.id || practitioner?.id;
    let b_type = '-';
    if (this.bookingType === 'In Person') {
      b_type = 'p';
    } else if (this.bookingType === 'Online') {
      b_type = 'o';
    }
    localStorage.setItem('came_from', this.router.url);
    this.shared.goToBooking({
      business: practitioner?.business.slug,
      service: `${service_id}a${b_type}`,
      prac: prac_id,
      date: moment(this.slotsAvailability.appt_date).format('YYYY-MM-DD'),
      time: time ? moment(time, 'hh:mm A').format('hh:mmA') : ''
    });
  }

  // Convert degrees to radians
  toRad(deg: number): number {
    return (deg * Math.PI) / 180;
  }

  // Update marker styles based on proximity
  updateMarkerStyles() {
    const zoomLevel = this.map.getZoom();
    const threshold = 0.01 * Math.pow(2, 12 - zoomLevel); // Calculate proximity threshold based on zoom level
    this.markers.forEach((marker, index) => {
      let isClose = false;
      const coords = { ...marker.getLngLat() };

      for (let i = 0; i < this.coordinates.length; i++) {
        if (i !== index) {
          const distance = Math.sqrt(
            Math.pow(coords.lng - this.coordinates[i].coords.lng, 2) +
              Math.pow(coords.lat - this.coordinates[i].coords.lat, 2)
          );
          if (distance < threshold) {
            isClose = true;
            break;
          }
        }
      }
      const markerEl = marker.getElement();
      if (isClose) {
        markerEl.classList.add('small-dot-marker');
        markerEl.classList.remove('normal-marker');
      } else {
        markerEl.classList.add('normal-marker');
        markerEl.classList.remove('small-dot-marker');
      }
    });
  }

  goToPrev(e: any) {
    if (e.scrollLeft <= 0) return;
    e.scrollLeft -= 300;
  }

  goToNext(e: any) {
    e.scrollLeft += 300;
  }

  checkIfItHasScroll(parent: any, child: any) {
    return child.scrollWidth > parent.clientWidth;
  }
  getDisplayName(partner: any): string {
    if (!partner) {
      return '';
    }

    const rawPreferred = (partner.preferred_name || '').toString().trim();
    const rawName = (partner.name || '').toString().trim();

    // Split on any whitespace and filter out empty tokens
    const prefParts = rawPreferred.split(/\s+/).filter(Boolean);
    const nameParts = rawName.split(/\s+/).filter(Boolean);

    // 1) If preferred name has two or more words, display it as-is
    if (prefParts.length >= 2) {
      return rawPreferred;
    }

    // 2) If preferred name is a single word AND the full name has two or more words,
    //    display: `<preferred> <last word of full name>`
    if (prefParts.length === 1 && nameParts.length >= 2) {
      const lastName = nameParts[nameParts.length - 1];
      return `${prefParts[0]} ${lastName}`;
    }

    // Fallbacks: show preferred if present, otherwise the full name
    if (rawPreferred) {
      return rawPreferred;
    }
    return rawName;
  }
}
